// Signage → Retail media (/signage/anuncios). Vende espacio a marcas/laboratorios:
// campañas de anuncio (banner o mensaje) con flujo de aprobación, share-of-voice
// (1 anuncio cada N escenas), segmentación (empresa/grupo/pantalla) y fechas. El
// reproductor las intercala en el loop; el proof-of-play cuenta las impresiones y
// esta pantalla entrega el reporte al anunciante.
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, CheckCircle2, Megaphone, Pause, Pencil, Play, Plus, Send, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, fieldCls } from '@/features/pos/shared';
import { MediaAsset, RefRow, sigInsert, sigList, sigRpc, sigUpdate } from './shared';
import { MediaPicker } from './MediaPicker';

interface Anuncio {
  id: string; anunciante: string; nombre: string; tipo: 'banner' | 'mensaje';
  media: string | null; titulo: string | null; texto: string | null; url: string | null;
  fecha_inicio: string; fecha_fin: string; sov_1_en_n: number; dur: number;
  ambito: 'empresa' | 'grupo' | 'pantalla'; grupo_id: string | null; pantalla_id: string | null;
  estado: 'borrador' | 'pendiente' | 'aprobada' | 'rechazada' | 'pausada'; nota_rechazo: string | null;
}
interface ReporteRow { id: string; anunciante: string; nombre: string; estado: string; fecha_inicio: string; fecha_fin: string; impresiones: number; pantallas: number; dias: number }

const ESTADOS: Record<Anuncio['estado'], { l: string; c: string }> = {
  borrador: { l: 'Borrador', c: 'bg-muted text-muted-foreground' },
  pendiente: { l: 'Pendiente', c: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  aprobada: { l: 'Aprobada', c: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  rechazada: { l: 'Rechazada', c: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  pausada: { l: 'Pausada', c: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};
const AMBITOS = [{ v: 'empresa', l: 'Toda la empresa' }, { v: 'grupo', l: 'Un grupo de pantallas' }, { v: 'pantalla', l: 'Una pantalla' }];
const hoy = () => new Date().toISOString().slice(0, 10);
const enDias = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export function Anuncios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tab, setTab] = useState<'campanas' | 'reporte'>('campanas');
  const [edit, setEdit] = useState<Partial<Anuncio> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [pickBanner, setPickBanner] = useState(false);
  const [desde, setDesde] = useState(enDias(-29));
  const [hasta, setHasta] = useState(hoy());

  const { data: anuncios = [] } = useQuery<Anuncio[]>({ queryKey: ['sig-anuncios'], queryFn: () => sigList<Anuncio>('anuncio?select=*&deleted_at=is.null&order=created_at.desc') });
  const { data: grupos = [] } = useQuery<RefRow[]>({ queryKey: ['sig-grupos'], queryFn: () => sigList<RefRow>('grupo?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: pantallas = [] } = useQuery<RefRow[]>({ queryKey: ['sig-pantallas'], queryFn: () => sigList<RefRow>('pantalla?select=id,nombre&deleted_at=is.null&order=nombre') });
  const { data: reporte = [], refetch: refReporte } = useQuery<ReporteRow[]>({
    queryKey: ['sig-anuncios-reporte', desde, hasta], enabled: tab === 'reporte',
    queryFn: () => sigRpc<ReporteRow[]>('sig_anuncios_reporte', { _desde: desde, _hasta: hasta }),
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-anuncios'] });
  const grupoNom = (id: string | null) => grupos.find((g) => g.id === id)?.nombre ?? '—';
  const pantallaNom = (id: string | null) => pantallas.find((p) => p.id === id)?.nombre ?? '—';
  const alcance = (a: Anuncio) => a.ambito === 'empresa' ? 'Toda la empresa' : a.ambito === 'grupo' ? `Grupo: ${grupoNom(a.grupo_id)}` : `Pantalla: ${pantallaNom(a.pantalla_id)}`;

  const subirArte = (file: File) => {
    if (file.size > 3 * 1024 * 1024) { toast.error('La imagen no debe superar 3 MB'); return; }
    const rd = new FileReader();
    rd.onload = () => setEdit((e) => ({ ...e, media: String(rd.result) }));
    rd.readAsDataURL(file);
  };

  const guardar = async () => {
    if (!edit?.anunciante || !edit.nombre) { toast.error('Anunciante y nombre son obligatorios'); return; }
    if ((edit.tipo ?? 'banner') === 'banner' && !edit.media) { toast.error('Sube el arte del banner'); return; }
    if ((edit.tipo ?? 'banner') === 'mensaje' && !edit.titulo) { toast.error('El mensaje necesita un título'); return; }
    setGuardando(true);
    try {
      const amb = edit.ambito || 'empresa';
      const body = {
        anunciante: edit.anunciante, nombre: edit.nombre, tipo: edit.tipo || 'banner',
        media: (edit.tipo ?? 'banner') === 'banner' ? edit.media : null,
        titulo: edit.titulo || null, texto: edit.texto || null, url: edit.url || null,
        fecha_inicio: edit.fecha_inicio || hoy(), fecha_fin: edit.fecha_fin || enDias(30),
        sov_1_en_n: edit.sov_1_en_n ?? 6, dur: edit.dur ?? 8, ambito: amb,
        grupo_id: amb === 'grupo' ? edit.grupo_id || null : null,
        pantalla_id: amb === 'pantalla' ? edit.pantalla_id || null : null,
      };
      if (edit.id) await sigUpdate('anuncio', edit.id, body); else await sigInsert('anuncio', body);
      setEdit(null); invalidar(); toast.success('Anuncio guardado');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const accion = async (a: Anuncio, act: string, nota?: string) => {
    try { await sigRpc('sig_anuncio_accion', { _id: a.id, _accion: act, _nota: nota ?? null }); invalidar(); toast.success('Estado actualizado'); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const rechazar = async (a: Anuncio) => {
    const v = await dlg.pedir('Rechazar anuncio', [{ key: 'motivo', label: 'Motivo del rechazo', minimo: 0 }]);
    if (v) void accion(a, 'rechazar', v.motivo);
  };
  const eliminar = (a: Anuncio) => sigUpdate('anuncio', a.id, { deleted_at: new Date().toISOString() }).then(invalidar).then(() => toast.success('Anuncio eliminado')).catch((e) => toast.error(getErrorMessage(e)));

  const totReporte = useMemo(() => reporte.reduce((s, r) => s + Number(r.impresiones), 0), [reporte]);

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Retail media</h1>
        {tab === 'campanas' && (
          <Button className="ml-auto" onClick={() => setEdit({ tipo: 'banner', ambito: 'empresa', sov_1_en_n: 6, dur: 8, fecha_inicio: hoy(), fecha_fin: enDias(30) })}>
            <Plus className="w-4 h-4 mr-1" /> Nuevo anuncio
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">Vende espacio a marcas y laboratorios: sus anuncios se intercalan en el loop (1 cada N escenas) y cuentas las impresiones para facturarles. El anuncio solo sale al aire cuando un administrador lo <strong>aprueba</strong> y está dentro de fechas.</p>

      <div className="flex gap-1 border-b">
        <button className={`px-4 py-2 text-sm border-b-2 ${tab === 'campanas' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setTab('campanas')}>Campañas</button>
        <button className={`px-4 py-2 text-sm border-b-2 ${tab === 'reporte' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`} onClick={() => setTab('reporte')}><BarChart3 className="w-4 h-4 inline mr-1" />Reporte</button>
      </div>

      {tab === 'campanas' ? (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground border-b bg-muted/30">
              <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Anunciante / campaña</th>
              <th className="py-2 px-3 text-left">Tipo</th><th className="py-2 px-3 text-left">Alcance</th>
              <th className="py-2 px-3 text-center">SOV</th><th className="py-2 px-3 text-left">Vigencia</th>
              <th className="py-2 px-3 text-center">Estado</th><th className="py-2 px-3" />
            </tr></thead>
            <tbody>
              {anuncios.map((a, i) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 px-3"><div className="font-medium">{a.anunciante}</div><div className="text-xs text-muted-foreground">{a.nombre}</div></td>
                  <td className="py-2 px-3 text-muted-foreground">{a.tipo === 'banner' ? 'Banner' : 'Mensaje'}</td>
                  <td className="py-2 px-3 text-muted-foreground">{alcance(a)}</td>
                  <td className="py-2 px-3 text-center tabular-nums">1/{a.sov_1_en_n}</td>
                  <td className="py-2 px-3 text-muted-foreground tabular-nums">{a.fecha_inicio} → {a.fecha_fin}</td>
                  <td className="py-2 px-3 text-center"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ESTADOS[a.estado].c}`}>{ESTADOS[a.estado].l}</span>{a.estado === 'rechazada' && a.nota_rechazo && <div className="text-[11px] text-red-500 mt-0.5" title={a.nota_rechazo}>{a.nota_rechazo.slice(0, 40)}</div>}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {(a.estado === 'borrador' || a.estado === 'rechazada') && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Enviar a aprobación" onClick={() => void accion(a, 'enviar')}><Send className="w-4 h-4 text-blue-500" /></button>}
                    {a.estado === 'pendiente' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Aprobar" onClick={() => void accion(a, 'aprobar')}><CheckCircle2 className="w-4 h-4 text-emerald-600" /></button>}
                    {a.estado === 'pendiente' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Rechazar" onClick={() => rechazar(a)}><XCircle className="w-4 h-4 text-red-500" /></button>}
                    {a.estado === 'aprobada' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Pausar" onClick={() => void accion(a, 'pausar')}><Pause className="w-4 h-4 text-amber-600" /></button>}
                    {a.estado === 'pausada' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Reactivar" onClick={() => void accion(a, 'reactivar')}><Play className="w-4 h-4 text-emerald-600" /></button>}
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(a)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => void eliminar(a)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </td>
                </tr>
              ))}
              {!anuncios.length && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin anuncios. Crea uno y envíalo a aprobación.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-end gap-3">
            <Campo label="Desde"><input className={fieldCls} type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
            <Campo label="Hasta"><input className={fieldCls} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            <Button variant="ghost" onClick={() => void refReporte()}>Actualizar</Button>
          </div>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-muted-foreground border-b bg-muted/30">
                <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Anunciante / campaña</th>
                <th className="py-2 px-3 text-left">Estado</th><th className="py-2 px-3 text-left">Vigencia</th>
                <th className="py-2 px-3 text-right">Impresiones</th><th className="py-2 px-3 text-right">Pantallas</th><th className="py-2 px-3 text-right">Días</th>
              </tr></thead>
              <tbody>
                {reporte.map((r, i) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 px-3"><div className="font-medium">{r.anunciante}</div><div className="text-xs text-muted-foreground">{r.nombre}</div></td>
                    <td className="py-2 px-3 text-muted-foreground">{ESTADOS[r.estado as Anuncio['estado']]?.l ?? r.estado}</td>
                    <td className="py-2 px-3 text-muted-foreground tabular-nums">{r.fecha_inicio} → {r.fecha_fin}</td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium">{Number(r.impresiones).toLocaleString('es')}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{r.pantallas}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{r.dias}</td>
                  </tr>
                ))}
                {!reporte.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin impresiones en el rango.</td></tr>}
                {reporte.length > 0 && (
                  <tr className="border-t-2 font-semibold bg-muted/30">
                    <td className="py-2 px-3" colSpan={4}>Totales ({reporte.length})</td>
                    <td className="py-2 px-3 text-right tabular-nums">{totReporte.toLocaleString('es')}</td>
                    <td className="py-2 px-3" colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {edit && (
        <Modal title={edit.id ? 'Editar anuncio' : 'Nuevo anuncio'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Anunciante (marca)"><input className={fieldCls} value={edit.anunciante ?? ''} onChange={(e) => setEdit({ ...edit, anunciante: e.target.value })} autoFocus /></Campo>
              <Campo label="Nombre de la campaña"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></Campo>
              <Campo label="Tipo">
                <select className={fieldCls} value={edit.tipo ?? 'banner'} onChange={(e) => setEdit({ ...edit, tipo: e.target.value as Anuncio['tipo'] })}>
                  <option value="banner">Banner (imagen a pantalla completa)</option>
                  <option value="mensaje">Mensaje (título + texto)</option>
                </select>
              </Campo>
              <Campo label="Duración (seg)"><input className={fieldCls} type="number" min={3} max={30} value={edit.dur ?? 8} onChange={(e) => setEdit({ ...edit, dur: Number(e.target.value) })} /></Campo>
            </div>

            {(edit.tipo ?? 'banner') === 'banner' ? (
              <Campo label="Arte del banner (imagen ≤ 3 MB)">
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && subirArte(e.target.files[0])} className="text-sm" />
                  <Button type="button" size="sm" variant="outline" onClick={() => setPickBanner(true)}>Elegir de biblioteca</Button>
                  {edit.media && <img src={edit.media} alt="preview" className="h-14 rounded border object-cover" />}
                </div>
              </Campo>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                <Campo label="Título"><input className={fieldCls} value={edit.titulo ?? ''} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })} /></Campo>
                <Campo label="Texto (opcional)"><textarea className={fieldCls} rows={2} value={edit.texto ?? ''} onChange={(e) => setEdit({ ...edit, texto: e.target.value })} /></Campo>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Campo label="Desde"><input className={fieldCls} type="date" value={edit.fecha_inicio ?? hoy()} onChange={(e) => setEdit({ ...edit, fecha_inicio: e.target.value })} /></Campo>
              <Campo label="Hasta"><input className={fieldCls} type="date" value={edit.fecha_fin ?? enDias(30)} onChange={(e) => setEdit({ ...edit, fecha_fin: e.target.value })} /></Campo>
              <Campo label="Frecuencia (1 anuncio cada N escenas)"><input className={fieldCls} type="number" min={2} max={30} value={edit.sov_1_en_n ?? 6} onChange={(e) => setEdit({ ...edit, sov_1_en_n: Number(e.target.value) })} /></Campo>
              <Campo label="Alcance">
                <select className={fieldCls} value={edit.ambito ?? 'empresa'} onChange={(e) => setEdit({ ...edit, ambito: e.target.value as Anuncio['ambito'] })}>
                  {AMBITOS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                </select>
              </Campo>
              {edit.ambito === 'grupo' && (
                <Campo label="Grupo">
                  <select className={fieldCls} value={edit.grupo_id ?? ''} onChange={(e) => setEdit({ ...edit, grupo_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {grupos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                  </select>
                </Campo>
              )}
              {edit.ambito === 'pantalla' && (
                <Campo label="Pantalla">
                  <select className={fieldCls} value={edit.pantalla_id ?? ''} onChange={(e) => setEdit({ ...edit, pantalla_id: e.target.value })}>
                    <option value="">— Selecciona —</option>
                    {pantallas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                  </select>
                </Campo>
              )}
              <Campo label="URL de destino (opcional, para QR/informe)"><input className={fieldCls} value={edit.url ?? ''} onChange={(e) => setEdit({ ...edit, url: e.target.value })} placeholder="https://…" /></Campo>
            </div>
            <p className="text-xs text-muted-foreground">Al guardar queda en <strong>borrador</strong>. Envíalo a aprobación desde la lista; un administrador lo aprueba para que salga al aire.</p>
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}
      {pickBanner && edit && (
        <MediaPicker tipo="imagen" onClose={() => setPickBanner(false)}
          onPick={(a: MediaAsset) => setEdit((prev) => ({ ...prev, tipo: 'banner', media: a.datos }))} />
      )}
    </div>
  );
}
